import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { loginUser, clearError } from '../../../store/slices/authSlice';
import { LoginCredentials, UserRole } from '../../../types/portal';
import { clearLoginRedirectFlag } from '../../../services/authRedirect';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const InputGroup = styled.div<{ $hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;

  label {
    font-size: 0.85rem;
    font-weight: 700;
    color: #444;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  input {
    padding: 0.875rem 1rem;
    border-radius: 12px;
    border: 2px solid ${props => props.$hasError ? '#ef4444' : '#eee'};
    font-size: 1rem;
    transition: all 0.2s;
    background: #f9f9f9;

    &:focus {
      outline: none;
      border-color: ${props => props.$hasError ? '#ef4444' : 'var(--primary-orange)'};
      background: #fff;
      box-shadow: 0 0 0 4px ${props => props.$hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(var(--primary-orange-rgb, 236, 92, 44), 0.1)'};
    }
  }
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fee2e2;
  color: #ef4444;
  padding: 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const SubmitButton = styled.button`
  background: var(--primary-orange);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1.1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(var(--primary-orange-rgb, 236, 92, 44), 0.2);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  bottom: 0.75rem;
  background: transparent;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  color: #666;
  &:hover { color: #111; }
`;


const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 10px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LinksRow = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
  gap: 2rem;

  button {
    background: transparent;
    border: none;
    padding: 0;
    color: #666;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: color 0.2s;

    &:hover { color: var(--primary-orange); }
  }
`;

interface LoginFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
  onRegister?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onForgotPassword, onRegister }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error, user } = useAppSelector((state) => state.auth);

  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState<{ username: boolean; password: boolean }>({ username: false, password: false });
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});

  const validate = (name: string, value: string) => {
    if (name === 'username') {
      if (!value.trim()) return t('portal.login.username_required', 'Email or Username is required');
      return undefined;
    }
    if (name === 'password') {
      if (!value) return t('portal.login.password_required', 'Password is required');
      return undefined;
    }
    return undefined;
  };

  useEffect(() => { dispatch(clearError()); }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && user) {
      clearLoginRedirectFlag();
      if (onSuccess) {
        onSuccess();
      } else {
        switch (user.role) {
          case UserRole.STUDENT: navigate('/portal/student'); break;
          case UserRole.TEACHER: navigate('/portal/teacher'); break;
          case UserRole.ADMIN: navigate('/portal/admin'); break;
          default: navigate('/');
        }
      }
    }
  }, [isAuthenticated, user, navigate, onSuccess]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validate(name, value) }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validate(name, value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ username: true, password: true });
    const uErr = validate('username', credentials.username);
    const pErr = validate('password', credentials.password);
    if (uErr || pErr) {
      setErrors({ username: uErr, password: pErr });
      return;
    }
    dispatch(loginUser(credentials));
  };

  return (
    <div>
      <Form onSubmit={handleSubmit}>
        {error && <ErrorBanner>{error}</ErrorBanner>}

        <InputGroup $hasError={!!(touched.username && errors.username)}>
          <label htmlFor="username">
            {t('portal.login.username', 'Email or Username')}
          </label>
          <input
            type="text"
            id="username"
            name="username"
            autoComplete="username"
            value={credentials.username}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={t('portal.login.username_placeholder', 'Your email or username') as string}
            disabled={isLoading}
          />
        </InputGroup>

        <InputGroup $hasError={!!(touched.password && errors.password)}>
          <label htmlFor="password">
            {t('portal.login.password', 'Password')}
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            autoComplete="current-password"
            value={credentials.password}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={t('portal.register.password_placeholder', '••••••••') as string}
            disabled={isLoading}
          />
          <PasswordToggle
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={(showPassword ? t('portal.login.hide_password', 'Hide password') : t('portal.login.show_password', 'Show password')) as string}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </PasswordToggle>
        </InputGroup>

        <SubmitButton type="submit" disabled={isLoading}>
          {isLoading && <Spinner />}
          {t('portal.login.submit', 'Sign In')}
        </SubmitButton>
      </Form>

      <LinksRow>
        {onForgotPassword && (
          <button type="button" onClick={onForgotPassword}>
            {t('portal.login.forgot_password', 'Forgot password?')}
          </button>
        )}
        {onRegister && (
          <button type="button" onClick={onRegister}>
            {t('home.ctaButton', 'Register')}
          </button>
        )}
      </LinksRow>
    </div>
  );
};

export default LoginForm;
